<style lang="scss" src="./company-editing-drawer.scss"></style>

<template>
  <a-tooltip
    title="Редактировать"
    placement="bottom"
    overlay-class-name="general-information-editing-tooltip"
  >
    <button
      type="button"
      class="general-information__company-editing-btn"
      aria-label="Редактировать данные компании"
      @click="openDrawer"
    >
      <img
        :src="`${ICONS_BASE}/edit.svg`"
        alt=""
        class="general-information__company-editing-icon"
      />
    </button>
  </a-tooltip>

  <a-drawer
    :width="800"
    :open="open"
    class="company-editing-drawer"
    :body-style="{ padding: '0px 40px 32px 40px' }"
    :footer-style="{ textAlign: 'left', padding: '20px 40px 40px 40px' }"
    :header-style="{ padding: '32px 40px', border: 'none' }"
    @close="handleClose"
  >
    <a-divider class="company-editing-drawer__divider-header" />

    <template #title>
      <span class="company-editing-drawer__title">Данные о компании</span>
    </template>

    <template #closeIcon>
      <span class="company-editing-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <a-form
      ref="drawerFormRef"
      class="company-editing-drawer__form"
      :model="form"
      :rules="rules"
      layout="vertical"
    >
      <div class="company-editing-drawer__section">
        <span class="company-editing-drawer__section--section-title">Общее</span>

        <div class="company-editing-drawer__section--section-content">
          <div class="company-editing-drawer__logo-row">
            <div class="company-editing-drawer__logo-preview">
              <img
                v-if="logoPreviewSrc"
                :src="logoPreviewSrc"
                class="company-editing-drawer__logo-image"
              />
              <img
                v-else
                :src="`${ICONS_BASE}/preview_img.svg`"
                class="company-editing-drawer__logo-icon"
              />
            </div>

            <div class="company-editing-drawer__logo-meta">
              <div v-if="hasLogoPreview" class="company-editing-drawer__logo-actions">
                <button type="button" class="btn btn-secondary btn-medium" @click="openLogoPicker">
                  Заменить лого
                </button>
                <button type="button" class="btn btn-outline btn-medium" @click="removeLogo">
                  Удалить
                </button>
              </div>
              <button
                v-else
                type="button"
                class="btn btn-secondary btn-medium"
                @click="openLogoPicker"
              >
                Выбрать лого
              </button>

              <input
                ref="logoInputRef"
                type="file"
                class="company-editing-drawer__logo-input"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                hidden
                aria-hidden="true"
                @change="onLogoChange"
              />
              <p class="company-editing-drawer__logo-hint">Файл JPG или PNG до 10МБ</p>
              <p v-if="logoError" class="company-editing-drawer__logo-error">{{ logoError }}</p>
            </div>
          </div>

          <a-divider class="company-editing-drawer__field-divider" />

          <a-form-item
            class="stc-ant-field-skin"
            label="Полное название"
            name="company_name"
            required
          >
            <a-input v-model:value="form.company_name" placeholder="Полное название" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Сокращенное название" name="short_name">
            <a-input v-model:value="form.short_name" placeholder="Короткое название" />
          </a-form-item>

          <a-form-item
            class="stc-ant-field-skin"
            label="Описание проекта"
            name="project_description"
          >
            <a-textarea
              v-model:value="form.project_description"
              class="company-editing-drawer__textarea"
              placeholder="Тип проекта, ожидаемые результаты"
              :maxlength="PROJECT_DESCRIPTION_MAX_LENGTH"
              :auto-size="{ minRows: 7, maxRows: 10 }"
              show-count
            />
          </a-form-item>

          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item class="stc-ant-field-skin" label="ИНН" name="inn" required>
                <a-input v-model:value="form.inn" placeholder="0987654321" />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item class="stc-ant-field-skin" label="ОГРН" name="ogrn">
                <a-input v-model:value="form.ogrn" placeholder="Номер ОГРН" />
              </a-form-item>
            </a-col>
          </a-row>
        </div>
      </div>

      <a-divider class="company-editing-drawer__divider" />

      <div class="company-editing-drawer__section">
        <span class="company-editing-drawer__section--section-title">Контакты</span>

        <div class="company-editing-drawer__section--section-content">
          <a-form-item
            class="stc-ant-field-skin"
            label="Юридический адрес"
            name="legal_address"
            required
          >
            <a-input v-model:value="form.legal_address" placeholder="Адрес" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Фактический адрес" name="actual_address">
            <a-input v-model:value="form.actual_address" placeholder="Адрес" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Email" name="email_company">
            <a-input v-model:value="form.email_company" placeholder="company@example.com" />
          </a-form-item>

          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item class="stc-ant-field-skin" label="Телефон" name="phone_company">
                <a-input v-model:value="form.phone_company" placeholder="+7 (___) ___-__-__" />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item class="stc-ant-field-skin" label="Сайт" name="company_link">
                <a-input v-model:value="form.company_link" placeholder="site.example.com" />
              </a-form-item>
            </a-col>
          </a-row>
        </div>
      </div>
    </a-form>

    <template #footer>
      <a-row align="middle" justify="end" class="company-editing-drawer__toolbar">
        <a-col>
          <a-space>
            <button type="button" class="btn btn-outline btn-medium" @click="handleClose">
              Отмена
            </button>
            <button
              type="button"
              class="btn btn-primary btn-medium"
              :disabled="isSaveDisabled"
              @click="onSubmit"
            >
              Сохранить
            </button>
          </a-space>
        </a-col>
      </a-row>
    </template>
  </a-drawer>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { getFileExtension } from '@/js/utils/file-upload-utils';
import { ICONS_BASE, useEditingDrawer } from './composables/use-editing-drawer';

const { open, showDrawer, onClose: closeDrawer } = useEditingDrawer();

const drawerFormRef = ref<FormInstance>();
const isSubmitting = ref(false);
const isLoading = ref(false);

const PROJECT_DESCRIPTION_MAX_LENGTH = 512;
const COMPANY_FORM_URL = getApiUrl('/api/form/company/');
const LOGO_MAX_SIZE_BYTES = 10 * 1024 * 1024;
const LOGO_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'] as const;
const LOGO_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];

type CompanyFormResponse = {
  status: 'success' | 'error';
  msg:
    | {
        company_name?: string;
        project_description?: string;
        company_link?: string;
        inn?: string;
        ogrn?: string;
        short_name?: string;
        legal_address?: string;
        actual_address?: string;
        email_company?: string;
        phone_company?: string;
        logo?: string;
      }
    | string;
};

const initialFormState = () => ({
  file: null as File | null,
  file_name: '',
  company_name: '',
  short_name: '',
  project_description: '',
  inn: '',
  ogrn: '',
  legal_address: '',
  actual_address: '',
  email_company: '',
  phone_company: '',
  company_link: '',
});

type CompanyFormSnapshot = {
  company_name: string;
  short_name: string;
  project_description: string;
  inn: string;
  ogrn: string;
  legal_address: string;
  actual_address: string;
  email_company: string;
  phone_company: string;
  company_link: string;
  logoServerUrl: string;
};

const form = reactive(initialFormState());
const savedFormState = ref<CompanyFormSnapshot | null>(null);
const logoServerUrl = ref('');
const isLogoDeleted = ref(false);
const logoError = ref('');

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.company_name) &&
    isNotBlankString(form.inn) &&
    isNotBlankString(form.legal_address),
);

function captureFormState(): CompanyFormSnapshot {
  return {
    company_name: form.company_name,
    short_name: form.short_name,
    project_description: form.project_description,
    inn: form.inn,
    ogrn: form.ogrn,
    legal_address: form.legal_address,
    actual_address: form.actual_address,
    email_company: form.email_company,
    phone_company: form.phone_company,
    company_link: form.company_link,
    logoServerUrl: logoServerUrl.value,
  };
}

function hasLogoChanges(initial: CompanyFormSnapshot): boolean {
  return form.file !== null || (isLogoDeleted.value && Boolean(initial.logoServerUrl));
}

function isSameFormState(current: CompanyFormSnapshot, initial: CompanyFormSnapshot): boolean {
  return (
    current.company_name === initial.company_name &&
    current.short_name === initial.short_name &&
    current.project_description === initial.project_description &&
    current.inn === initial.inn &&
    current.ogrn === initial.ogrn &&
    current.legal_address === initial.legal_address &&
    current.actual_address === initial.actual_address &&
    current.email_company === initial.email_company &&
    current.phone_company === initial.phone_company &&
    current.company_link === initial.company_link &&
    current.logoServerUrl === initial.logoServerUrl
  );
}

const hasUnsavedChanges = computed(() => {
  const initial = savedFormState.value;
  if (!initial) return false;

  const current = captureFormState();
  return !isSameFormState(current, initial) || hasLogoChanges(initial);
});

const isEmailValidOrEmpty = computed(
  () => !isNotBlankString(form.email_company) || isValidEmail(form.email_company),
);

const isSaveDisabled = computed(
  () =>
    isLoading.value ||
    isSubmitting.value ||
    !isRequiredFieldsFilled.value ||
    !isEmailValidOrEmpty.value ||
    !hasUnsavedChanges.value ||
    Boolean(logoError.value),
);

const rules: Record<string, Rule[]> = {
  company_name: [{ required: true, message: 'Введите полное название', whitespace: true }],
  short_name: [{ required: false, message: 'Введите сокращенное название', whitespace: true }],
  project_description: [{ required: false, message: 'Введите описание проекта', whitespace: true }],
  inn: [{ required: true, message: 'Введите ИНН', whitespace: true }],
  legal_address: [{ required: true, message: 'Введите юридический адрес', whitespace: true }],
  actual_address: [{ required: false, message: 'Введите фактический адрес', whitespace: true }],
  email_company: [{ required: false, message: 'Введите e-mail', type: 'email' }],
  phone_company: [{ required: false, message: 'Введите телефон', whitespace: true }],
  company_link: [{ required: false, message: 'Введите сайт', whitespace: true }],
};

const logoInputRef = ref<HTMLInputElement | null>(null);
const logoObjectUrl = ref<string>('');

const logoPreviewSrc = computed(() => logoObjectUrl.value || logoServerUrl.value);
const hasLogoPreview = computed(() => Boolean(logoPreviewSrc.value));

function validateLogoFile(file: File): string | null {
  const extension = getFileExtension(file.name);
  const hasValidExtension = LOGO_ALLOWED_EXTENSIONS.includes(
    extension as (typeof LOGO_ALLOWED_EXTENSIONS)[number],
  );
  const hasValidMime = !file.type || LOGO_ALLOWED_MIME_TYPES.includes(file.type);

  if (!hasValidExtension || !hasValidMime) {
    return 'Допустимы только файлы JPG или PNG';
  }

  if (file.size > LOGO_MAX_SIZE_BYTES) {
    return 'Размер файла не должен превышать 10 Мб';
  }

  return null;
}

function clearLogoInput(): void {
  if (logoInputRef.value) {
    logoInputRef.value.value = '';
  }
}

const applyCompanyData = (data: CompanyFormResponse['msg']) => {
  if (!data || typeof data === 'string') return;

  form.company_name = data.company_name ?? '';
  form.short_name = data.short_name ?? '';
  form.project_description = data.project_description ?? '';
  form.inn = data.inn ?? '';
  form.ogrn = data.ogrn ?? '';
  form.legal_address = data.legal_address ?? '';
  form.actual_address = data.actual_address ?? '';
  form.email_company = data.email_company ?? '';
  form.phone_company = data.phone_company ?? '';
  form.company_link = data.company_link ?? '';
  form.file = null;
  form.file_name = '';
  logoServerUrl.value = data.logo ?? '';
  isLogoDeleted.value = false;

  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  clearLogoInput();
};

function setCompanyFieldValue(label: string, value: string): void {
  const companyMount = document.getElementById('company-editing-vue');
  const section = companyMount?.closest<HTMLElement>('.general-information__section');
  if (!section) return;

  section.querySelectorAll<HTMLElement>('.general-information__field').forEach((field) => {
    const labelEl = field.querySelector('.general-information__field-label');
    const valueEl = field.querySelector('.general-information__field-value');

    if (!labelEl || !valueEl) return;
    if (labelEl.textContent?.trim() !== label) return;
    if (valueEl.classList.contains('general-information__field-value--cluster')) return;

    valueEl.textContent = value;
  });
}

function syncCompanyDataToPage(): void {
  const residentName = document.querySelector('[data-resident-name]');
  if (residentName) {
    residentName.textContent = form.company_name;
  }

  const sidebarLogo = document.querySelector<HTMLImageElement>(
    '.residency-lk-sidebar__img_box img',
  );
  if (sidebarLogo) {
    if (logoServerUrl.value) {
      sidebarLogo.src = logoServerUrl.value;
      sidebarLogo.hidden = false;
    } else {
      sidebarLogo.removeAttribute('src');
      sidebarLogo.hidden = true;
    }
  }

  setCompanyFieldValue('Полное наименование', form.company_name);
  setCompanyFieldValue('Сокращенное название', form.short_name);
  setCompanyFieldValue('Описание проекта', form.project_description);
  setCompanyFieldValue('ИНН', form.inn);
  setCompanyFieldValue('ОГРН', form.ogrn);
  setCompanyFieldValue('Юридический адрес', form.legal_address);
  setCompanyFieldValue('Фактический адрес', form.actual_address);
  setCompanyFieldValue('Email', form.email_company);
  setCompanyFieldValue('Телефон', form.phone_company);
  setCompanyFieldValue('Сайт', form.company_link);
}

async function refreshLogoAfterSave(): Promise<void> {
  const response = await fetch(COMPANY_FORM_URL, {
    method: 'GET',
    credentials: 'same-origin',
  });
  const result = (await response.json()) as CompanyFormResponse;

  if (!response.ok || result.status !== 'success' || typeof result.msg === 'string') {
    return;
  }

  logoServerUrl.value = result.msg.logo ?? '';
}

function commitSavedFormState(): void {
  form.file = null;
  form.file_name = '';
  isLogoDeleted.value = false;

  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  clearLogoInput();
  savedFormState.value = captureFormState();
}

const openLogoPicker = () => {
  logoInputRef.value?.click();
};

const onLogoChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  logoError.value = '';

  if (!file) {
    return;
  }

  const validationError = validateLogoFile(file);
  if (validationError) {
    logoError.value = validationError;
    showStcErrorNotification(validationError);
    clearLogoInput();
    return;
  }

  form.file = file;
  form.file_name = file.name;
  logoServerUrl.value = '';
  isLogoDeleted.value = false;

  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  logoObjectUrl.value = URL.createObjectURL(file);
};

const removeLogo = () => {
  form.file = null;
  form.file_name = '';
  isLogoDeleted.value = true;
  logoError.value = '';

  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  clearLogoInput();
  logoServerUrl.value = '';
};

const resetLogoState = () => {
  if (logoObjectUrl.value) {
    URL.revokeObjectURL(logoObjectUrl.value);
    logoObjectUrl.value = '';
  }

  clearLogoInput();
  logoServerUrl.value = '';
  isLogoDeleted.value = false;
  logoError.value = '';
};

const resetFormState = () => {
  Object.assign(form, initialFormState());
  savedFormState.value = null;
  resetLogoState();

  nextTick(() => {
    drawerFormRef.value?.clearValidate();
  });
};

const loadCompanyData = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(COMPANY_FORM_URL, {
      method: 'GET',
      credentials: 'same-origin',
    });
    const result = (await response.json()) as CompanyFormResponse;
    if (!response.ok || result.status !== 'success') {
      throw new Error(
        typeof result.msg === 'string' ? result.msg : 'Не удалось загрузить данные компании',
      );
    }
    applyCompanyData(result.msg);
    savedFormState.value = captureFormState();
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const openDrawer = async () => {
  showDrawer();
  await loadCompanyData();
};

const onSubmit = async () => {
  if (!drawerFormRef.value) return;

  if (form.file) {
    const validationError = validateLogoFile(form.file);
    if (validationError) {
      logoError.value = validationError;
      showStcErrorNotification(validationError);
      return;
    }
  }

  try {
    await drawerFormRef.value.validate();
  } catch {
    return;
  }

  isSubmitting.value = true;
  try {
    const sessid = getBitrixSessid();
    if (!sessid) {
      throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
    }

    const body = new FormData();
    body.append('company_name', form.company_name);
    body.append('short_name', form.short_name);
    body.append('project_description', form.project_description);
    body.append('inn', form.inn);
    body.append('ogrn', form.ogrn);
    body.append('legal_address', form.legal_address);
    body.append('actual_address', form.actual_address);
    body.append('email_company', form.email_company);
    body.append('phone_company', form.phone_company);
    body.append('company_link', form.company_link);

    if (form.file) {
      body.append('file', form.file);
    } else if (isLogoDeleted.value) {
      body.append('delete_logo', '1');
    }

    body.append('sessid', sessid);

    const response = await fetch(COMPANY_FORM_URL, {
      method: 'POST',
      credentials: 'same-origin',
      body,
      headers: {
        'X-Bitrix-Csrf-Token': sessid,
      },
    });

    const result = (await response.json()) as CompanyFormResponse;
    if (!response.ok || result.status !== 'success') {
      throw new Error(
        typeof result.msg === 'string' ? result.msg : 'Не удалось сохранить данные компании',
      );
    }

    const successMessage = typeof result.msg === 'string' ? result.msg : 'Данные успешно сохранены';

    if (form.file || isLogoDeleted.value) {
      await refreshLogoAfterSave();
    }

    commitSavedFormState();
    syncCompanyDataToPage();
    showStcSuccessNotification(successMessage);
    closeDrawer();
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Ошибка соединения');
  } finally {
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  closeDrawer();
  resetFormState();
};

onBeforeUnmount(() => {
  resetLogoState();
});
</script>
